import React, { useEffect, useState } from 'react';
import { Phone, MessageCircle, MapPin, Clock, CheckCircle, Wrench, Key, Settings, FileCheck, Stethoscope, Star, ArrowRight, Shield, Zap, Award, Users, Instagram, Menu, X, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { generateLocalBusiness } from '../utils/structuredData';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const whatsappNumber = "5531971823249";
  const whatsappMessage = encodeURIComponent("Olá! Gostaria de solicitar um orçamento para locação/manutenção de lavadora de piso.");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+5531971823249';
  };

  const handleMapClick = () => {
    window.open('https://maps.google.com/?q=Rua+João+de+Carvalho+Silva,+274+São+Tarcísio+Mário+Campos+MG', '_blank');
  };

  const services = [
  {
    icon: <Key className="w-8 h-8" />,
    title: "Loca\xE7\xE3o de Lavadoras e Varredeiras de Piso",
    description: "Equipamentos profissionais para sua empresa com manutenção inclusa"
  },
  {
    icon: <Wrench className="w-8 h-8" />,
    title: "Manutenção Técnica Especializada",
    description: "Equipe qualificada com experiência em grandes marcas"
  },
  {
    icon: <Settings className="w-8 h-8" />,
    title: "Venda de Peças",
    description: "Peças originais para garantir a durabilidade do seu equipamento"
  },
  {
    icon: <FileCheck className="w-8 h-8" />,
    title: "Revisão Completa",
    description: "Check-up preventivo para evitar paradas inesperadas"
  },
  {
    icon: <Stethoscope className="w-8 h-8" />,
    title: "Diagnóstico Técnico Avançado",
    description: "Identificação precisa de problemas para soluções rápidas"
  }];


  const differentials = [
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Atendimento Rápido",
    description: "Resposta ágil para não parar sua operação"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Suporte Especializado",
    description: "Técnicos certificados e experientes"
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: "Experiência Comprovada",
    description: "Atuação com grandes marcas do mercado"
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Máquina Reserva",
    description: "Equipamento backup quando necessário"
  }];

  // Structured Data - Organization (para logo no Google)
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "WellTec",
    "legalName": "WellTec - Locações e Serviços Técnicos",
    "url": "https://www.wellttec.com",
    "logo": "https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+55-31-97182-3249",
      "contactType": "customer service",
      "areaServed": "BR",
      "availableLanguage": "Portuguese"
    },
    "sameAs": [
      "https://www.instagram.com/welltecbrasil/"
    ]
  };

  // Structured Data - LocalBusiness
  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "WellTec - Locações e Serviços Técnicos",
    "image": "https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png",
    "logo": "https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png",
    "url": "https://www.wellttec.com",
    "telephone": "+5531971823249",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rua João de Carvalho Silva, 274",
      "addressLocality": "Mário Campos",
      "addressRegion": "MG",
      "postalCode": "32400-000",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -20.0354,
      "longitude": -44.1994
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "08:00",
      "closes": "18:00"
    },
    "areaServed": [
      { "@type": "City", "name": "Belo Horizonte" },
      { "@type": "City", "name": "Contagem" },
      { "@type": "City", "name": "Betim" },
      { "@type": "City", "name": "Nova Lima" },
      { "@type": "City", "name": "Mário Campos" }
    ],
    "sameAs": [
      "https://www.instagram.com/welltecbrasil/"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        {...seoData["/"]}
        ogImage="https://www.wellttec.com/og-image.jpg"
      />
      
      {/* Structured Data - dentro do Helmet via react-helmet-async */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessData)}
        </script>
      </Helmet>
      
      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-4 sm:right-6 z-50 animate-bounce-slow">
        <Button
          onClick={handleWhatsAppClick}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-14 h-14 sm:w-16 sm:h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110">

          <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png"
              alt="WellTec Logo"
              className="h-16 w-auto" />

          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">
              Início
            </Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">
              Serviços
            </Link>
            <Link to="/equipamentos-disponiveis" className="hover:text-blue-400 transition-colors">
              Catálogo
            </Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">
              Limpeza Pós-Obra
            </Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">
              Locação
            </Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">
              Blog
            </Link>
            <a
              href="https://www.instagram.com/welltecbrasil/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
              title="Instagram @welltecbrasil">

              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button
                onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Serviços
              </button>
              <button
                onClick={() => { navigate('/equipamentos-disponiveis'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Catálogo
              </button>
              <button
                onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400">
                Limpeza Pós-Obra
              </button>
              <button
                onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Locação
              </button>
              <button
                onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Blog
              </button>
              <a 
                href="#diferenciais" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors block">
                Quem Somos
              </a>
              <a 
                href="#contato" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors block">
                Contato
              </a>
              <a
                href="https://www.instagram.com/welltecbrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors flex items-center">
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </a>
              <Button 
                onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }}
                className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section
        className="relative bg-slate-900 text-white py-20 md:py-32 overflow-hidden min-h-[550px] md:min-h-[650px]"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.35), rgba(30, 58, 138, 0.30)), url('https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/ll1nyd79_Limpeza-pos%20obra%20lavadora%20de%20piso%20galpao.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1e293b'
        }}>

        <div className={`container mx-auto px-6 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-3xl">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Sua limpeza profissional começa aqui
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Locação e manutenção de lavadoras de piso com suporte técnico especializado
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-300 hover:scale-105 w-full sm:w-auto">

                <MessageCircle className="w-5 h-5 mr-2" />
                Fale no WhatsApp
              </Button>
              <Button
                onClick={() => {
                  const formSection = document.querySelector('form');
                  if (formSection) {
                    formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-300 w-full sm:w-auto">

                Solicitar Orçamento
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* NOVA SEÇÃO: Principais Soluções - Cards Hero */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Nossas Principais Soluções
            </h2>
            <p className="text-xl text-gray-600">
              Escolha o serviço ideal para sua empresa
            </p>
          </div>

          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
            {/* Card 1: Limpeza Pós-Obra */}
            <div 
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-500 hover:scale-105 hover:shadow-3xl h-[500px]"
              onClick={() => navigate('/limpeza-pos-obra')}
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(22, 163, 74, 0.95), rgba(21, 128, 61, 0.90)), url('https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/sqhpxfrr_Limpeza-pos%20obra%20lavadora%20de%20piso%20galpao.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              <div className="relative h-full flex flex-col justify-between p-8">
                <div>
                  <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    Limpeza Pós-Obra
                  </h3>
                  
                  <p className="text-lg text-green-50 mb-4 leading-relaxed">
                    Limpeza mecanizada para galpões, indústrias e grandes áreas
                  </p>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-200" />
                      <span>Equipamentos Profissionais</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-200" />
                      <span>Grandes Áreas</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-200" />
                      <span>Remoção de Cimento</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <Button 
                    onClick={() => navigate('/limpeza-pos-obra')}
                    size="lg"
                    className="bg-white text-green-700 hover:bg-green-50 font-bold w-full"
                  >
                    Ver Serviços
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                🔥 NOVO
              </div>
            </div>

            {/* Card 2: Locação de Equipamentos */}
            <div 
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-500 hover:scale-105 hover:shadow-3xl h-[500px]"
              onClick={() => navigate('/locacao-lavadora-piso')}
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(30, 64, 175, 0.95), rgba(29, 78, 216, 0.90)), url('https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/6c6006e7-0128-4018-adb3-c63a42fece8d/IMG-20250626-WA0092.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              <div className="relative h-full flex flex-col justify-between p-8">
                <div>
                  <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                    <Key className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    Locação de Equipamentos
                  </h3>
                  
                  <p className="text-lg text-blue-50 mb-4 leading-relaxed">
                    Lavadoras e varredeiras profissionais com manutenção inclusa
                  </p>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-blue-200" />
                      <span>Equipamentos Revisados</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-blue-200" />
                      <span>Manutenção Inclusa</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-blue-200" />
                      <span>Suporte Técnico</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <Button 
                    onClick={() => navigate('/locacao-lavadora-piso')}
                    size="lg"
                    className="bg-white text-blue-700 hover:bg-blue-50 font-bold w-full"
                  >
                    Ver Locação
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                ⭐ POPULAR
              </div>
            </div>

            {/* Card 3: Manutenção e Reforma */}
            <div 
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-500 hover:scale-105 hover:shadow-3xl h-[500px]"
              onClick={() => navigate('/servicos')}
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(234, 88, 12, 0.95), rgba(194, 65, 12, 0.90)), url('https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/22b71c3d-f9b8-441c-947e-36a2ce94bc21/DSC09318.JPG')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              <div className="relative h-full flex flex-col justify-between p-8">
                <div>
                  <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                    <Settings className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    Manutenção & Reforma
                  </h3>
                  
                  <p className="text-lg text-orange-50 mb-4 leading-relaxed">
                    Assistência técnica especializada em lavadoras e varredeiras industriais
                  </p>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-orange-200" />
                      <span>Manutenção Corretiva e Preventiva</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-orange-200" />
                      <span>Reforma Completa</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-orange-200" />
                      <span>Peças Originais</span>
                    </li>
                    <li className="flex items-center text-white text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-orange-200" />
                      <span>Garantia Técnica</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <Button 
                    onClick={() => navigate('/servicos')}
                    size="lg"
                    className="bg-white text-orange-700 hover:bg-orange-50 font-bold w-full"
                  >
                    Ver Serviços
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                🔧 ESPECIALIZADO
              </div>
            </div>
          </div>

          {/* CTA Adicional */}
          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg mb-6">
              Não sabe qual solução é ideal para você?
            </p>
            <Button 
              onClick={handleWhatsAppClick}
              variant="outline"
              size="lg"
              className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white text-lg px-10 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Falar com Especialista no WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Formulário de Contato Comercial */}
      <ContactForm />

      {/* Problem Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Limpeza manual está custando caro para sua empresa
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-red-500">
                <p className="text-lg font-semibold text-slate-700">Processos lentos e inconsistentes</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-red-500">
                <p className="text-lg font-semibold text-slate-700">Alto custo operacional</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-red-500">
                <p className="text-lg font-semibold text-slate-700">Baixa produtividade da equipe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                A solução está na WellTec
              </h3>
              <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/6c6006e7-0128-4018-adb3-c63a42fece8d/IMG-20250626-WA0092.jpg?w=600"
                  alt="Oficina WellTec com equipamentos profissionais"
                  className="rounded-lg shadow-xl" />

              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-slate-900 mb-2">Locação de lavadoras e varredeiras profissionais</h4>
                    <p className="text-gray-600">Equipamentos de ponta das principais marcas sem investimento inicial alto</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-slate-900 mb-2">Assistência técnica especializada</h4>
                    <p className="text-gray-600">Equipe técnica certificada com expertise em grandes marcas</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-slate-900 mb-2">Peças originais + Treinamento</h4>
                    <p className="text-gray-600">Peças certificadas e treinamento completo para operação segura</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-slate-900 mb-2">Atendimento rápido na região</h4>
                    <p className="text-gray-600">Cobertura em toda região metropolitana de BH</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-4">Nossos Serviços</h3>
            <p className="text-xl text-gray-300">Soluções completas para sua operação</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) =>
            <Card key={index} className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-6">
                  <div className="bg-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-4 text-white">
                    {service.icon}
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-white">{service.title}</h4>
                  <p className="text-gray-300">{service.description}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Ver Todos os Serviços Button */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/servicos')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6">

              Ver Todos os Serviços Detalhados
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* Equipment Showcase */}
          <div className="mt-20">
            <h4 className="text-3xl font-bold text-center mb-4">Equipamentos Disponíveis para Locação</h4>
            <p className="text-center text-gray-300 mb-12">Trabalhamos com as principais marcas do mercado</p>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/1769487042490-M231VKMH28PY4LYYRDOG/Screenshot_7.jpg"
                      alt="Lavadora AS510"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora AS510</h5>
                    <p className="text-gray-300 text-sm">Lavadora e secadora de pisos média, robusta e fácil de usar, ideal para limpeza diária de áreas internas com tráfego médio a intenso</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/1769487229177-AI3NAI2HDSZSQDZEQ7GD/SC450_masked.jpg"
                      alt="Lavadora SC450"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora SC450</h5>
                    <p className="text-gray-300 text-sm">Lavadora e secadora de pisos compacta e fácil de usar, ideal para limpeza diária de ambientes comerciais e industriais</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/1769487407430-IQCHK8JP6ER7SDWRMY47/Screenshot_8.jpg"
                      alt="Lavadora LST-51"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora Platinum LST-51</h5>
                    <p className="text-gray-300 text-sm">Lavadora automática de pisos robusta, semitracionária e de operação simples, projetada para limpeza profissional de áreas de tráfego médio</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://www.fimap.com/assets/repository/img/macchine/modelli/mr_75_disc_2026_entire_fisheye_2000x2000_rgb.jpg"
                      alt="Lavadora Fimap MR85"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora Fimap MR85</h5>
                    <p className="text-gray-300 text-sm">Lavadora automática de alto desempenho com tecnologia italiana, ideal para grandes áreas industriais e comerciais</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-yellow-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://d1y4tv7o00gnfq.cloudfront.net/mam/98998970/mainproduct/71392/d0.jpg"
                      alt="Lavadora Kärcher B300"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora Kärcher B300</h5>
                    <p className="text-gray-300 text-sm">Lavadora tripulada de alta produtividade da renomada marca alemã, perfeita para limpeza de grandes espaços</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-red-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://www.nilfisk.com/product-images-1920/SC6500-Cyl-OL-Rt-ps-Original-JPJHHJP.webp"
                      alt="Lavadora Nilfisk SC6500"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora Nilfisk SC6500</h5>
                    <p className="text-gray-300 text-sm">Lavadora de alta capacidade com tecnologia escandinava, robusta e eficiente para ambientes desafiadores</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-purple-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/ab7c88cb42818a1d_IMG_20260211_210748_723.jpg"
                      alt="Varredeira Tennant 6400"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Varredeira Tennant 6400</h5>
                    <p className="text-gray-300 text-sm">Varredeira tripulada de alto rendimento, ideal para limpeza de grandes áreas externas e internas com máxima eficiência</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/equipamentos-disponiveis" className="block">
                <Card className="bg-slate-800 border-slate-700 hover:border-orange-500 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <img
                      src="https://d1y4tv7o00gnfq.cloudfront.net/mam/98408630/mainproduct/48542/d0.jpg"
                      alt="Lavadora Kärcher BD530"
                      className="w-full h-48 object-contain bg-white rounded-lg mb-4" />
                    <h5 className="text-xl font-bold text-white mb-2">Lavadora Kärcher BD530</h5>
                    <p className="text-gray-300 text-sm">Lavadora compacta walk-behind da Kärcher, perfeita para áreas de médio porte com alta manobralidade</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>

          {/* Reforma de Lavadoras - Seção Especial */}
          <div className="mt-20 border-t-2 border-green-600 pt-20">
            <div className="text-center mb-12">
              <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                ⭐ REFORMA PREMIUM
              </div>
              <h4 className="text-3xl font-bold mb-4">Reforma de Lavadoras Tennant</h4>
              <p className="text-gray-300">Especialistas em devolver sua máquina com desempenho de fábrica</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card 
                className="bg-gradient-to-br from-blue-900 to-blue-800 border-blue-600 hover:border-blue-400 transition-all duration-300 cursor-pointer hover:scale-105"
                onClick={() => navigate('/reforma-tennant-m20')}
              >
                <CardContent className="p-8 text-center">
                  <div className="bg-blue-600 text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 font-bold text-2xl">
                    M20
                  </div>
                  <h5 className="text-2xl font-bold text-white mb-3">Reforma Tennant M20</h5>
                  <p className="text-blue-200 text-sm mb-6">Lavadora tripulada de alta capacidade. Reforma completa com garantia total.</p>
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white w-full">
                    Ver Detalhes
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-green-900 to-green-800 border-green-600 hover:border-green-400 transition-all duration-300 cursor-pointer hover:scale-105"
                onClick={() => navigate('/reforma-tennant-a5')}
              >
                <CardContent className="p-8 text-center">
                  <div className="bg-green-600 text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 font-bold text-2xl">
                    A5
                  </div>
                  <h5 className="text-2xl font-bold text-white mb-3">Reforma Tennant A5</h5>
                  <p className="text-green-200 text-sm mb-6">Lavadora pedestre tracionada. Alto rendimento com 80cm de faixa.</p>
                  <Button className="bg-green-500 hover:bg-green-600 text-white w-full">
                    Ver Detalhes
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section id="diferenciais" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          {/* Quem Somos */}
          <div className="max-w-4xl mx-auto mb-20 text-center">
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Quem Somos</h3>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
            <p className="text-lg text-gray-700 leading-relaxed">
              A <span className="font-bold text-slate-900">WellTec</span> é uma empresa especializada em <span className="font-semibold">locação e serviços técnicos de Varredeiras e Lavadoras de Piso</span>, oferecendo soluções completas para equipamentos industriais de limpeza. Com expertise no segmento e atuação sólida ao lado de grandes marcas, garantimos suporte confiável e manutenção qualificada. Atuamos também no fornecimento de peças, revisões completas e diagnósticos avançados. Localizados em <span className="font-semibold">Mário Campos – MG</span>, região metropolitana de BH, atendemos toda a região com eficiência, responsabilidade e compromisso com a qualidade.
            </p>
          </div>

          {/* Diferenciais */}
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Por que escolher a WellTec?</h3>
            <p className="text-xl text-gray-600">Diferenciais que fazem toda a diferença</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {differentials.map((diff, index) =>
            <div key={index} className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto text-blue-600">
                  {diff.icon}
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">{diff.title}</h4>
                <p className="text-gray-600 text-sm">{diff.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center items-center mb-6">
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-400" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Avaliação 4.4 no Google
            </h3>
            <p className="text-xl text-gray-600 mb-8">
              Empresas da região já confiam na WellTec
            </p>
            <div className="bg-gray-50 p-8 rounded-lg">
              <p className="text-lg text-gray-700 italic">
                "Atendimento profissional e suporte técnico excepcional. Nossas operações de limpeza melhoraram significativamente."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <MapPin className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-3xl md:text-4xl font-bold mb-4">Área de Atendimento</h3>
          <p className="text-xl">
            Atendemos Mário Campos, Contagem, Belo Horizonte e toda a região metropolitana
          </p>
        </div>
      </section>

      {/* Reforma Tennant M20 Banner */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url("https://customer-assets.emergentagent.com/job_tennant-m20-reforma/artifacts/n7nqav11_IMG_20250804_110454.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              SERVIÇO ESPECIALIZADO
            </div>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Reforma Completa Tennant M20
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              Especialistas em recuperação e remanufatura no padrão RECON. Devolvemos vida útil, desempenho e confiabilidade à sua máquina industrial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                onClick={() => navigate('/reforma-tennant-m20')}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6"
              >
                Saiba Mais sobre Reforma M20
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={handleWhatsAppClick}
                size="lg"
                variant="outline"
                className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white text-lg px-8 py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto pt-8 border-t border-slate-700">
              <div>
                <p className="text-3xl font-bold text-green-400">60%+</p>
                <p className="text-sm text-gray-400">Economia vs nova</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">2x</p>
                <p className="text-sm text-gray-400">Vida útil</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">30d</p>
                <p className="text-sm text-gray-400">Prazo típico</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.9), rgba(15, 23, 42, 0.9)), url('https://images.unsplash.com/photo-1770303430514-6ec150a21383?w=1600')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>

        <div className="container mx-auto px-6 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Pare de perder tempo com limpeza ineficiente
          </h3>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Fale com nossos especialistas agora e descubra como podemos otimizar sua operação
          </p>
          <Button
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-green-500 hover:bg-green-600 text-white text-xl px-12 py-8 transition-all duration-300 hover:scale-110 shadow-2xl">

            <MessageCircle className="w-6 h-6 mr-3" />
            Solicitar Orçamento Agora
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Entre em Contato</h3>
            <p className="text-xl text-gray-600">Estamos prontos para atender você</p>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent>
                <MapPin className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h4 className="text-xl font-bold text-slate-900 mb-3">Endereço</h4>
                <p className="text-gray-600 mb-4">
                  Rua João de Carvalho Silva, 274<br />
                  São Tarcísio – Mário Campos – MG
                </p>
                <Button onClick={handleMapClick} variant="outline" className="mt-2">
                  Ver no Mapa
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent>
                <Phone className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h4 className="text-xl font-bold text-slate-900 mb-3">Telefone</h4>
                <p className="text-gray-600 mb-4 text-lg font-semibold">
                  (31) 97182-3249
                </p>
                <Button onClick={handleCallClick} variant="outline" className="mt-2">
                  <Phone className="w-4 h-4 mr-2" />
                  Ligar Agora
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent>
                <Clock className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h4 className="text-xl font-bold text-slate-900 mb-3">Horário</h4>
                <p className="text-gray-600 mb-4">
                  Segunda a Sexta<br />
                  08:00 às 18:00
                </p>
                <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600 mt-2">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png"
                  alt="WellTec Logo"
                  className="h-20 w-auto" />

              </div>
              <p className="text-gray-400 mb-4">
                Especialistas em locação e manutenção de lavadoras de piso industriais.
              </p>
              <a
                href="https://www.instagram.com/welltecbrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">

                <Instagram className="w-5 h-5" />
                <span>@welltecbrasil</span>
              </a>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Links Rápidos</h5>
              <ul className="space-y-2">
                <li><Link to="/servicos" className="text-gray-400 hover:text-white transition-colors">Serviços</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="text-gray-400 hover:text-white transition-colors">Locação de Lavadora</Link></li>
                <li><Link to="/faq-locacao-lavadora-piso" className="text-gray-400 hover:text-white transition-colors">FAQ Locação</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/reforma-tennant-m20" className="text-green-400 hover:text-green-300 transition-colors">Reforma Tennant M20</Link></li>
                <li><a href="#diferenciais" className="text-gray-400 hover:text-white transition-colors">Quem Somos</a></li>
                <li><a href="#contato" className="text-gray-400 hover:text-white transition-colors">Contato</a></li>
                <li><a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Instagram</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Contato</h5>
              <ul className="space-y-2 text-gray-400">
                <li>(31) 97182-3249</li>
                <li>Mário Campos, MG</li>
                <li>Seg-Sex: 08:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;